import { DateTime } from 'luxon';

import store from 'store/storeConfig';

import {
  DeviceConditions,
  DeviceLocations,
  DeviceStatus,
  OperatorCode,
  OrgStatus,
  PatientStatus
} from './enums';

export const getDeviceLocationOptions = () => {
  const { intl } = store.getState();
  return [
    {
      label: intl.messages.undefinedKey,
      value: DeviceLocations.UNKNOWN
    },
    {
      label: intl.messages.officeKey,
      value: DeviceLocations.OFFICE
    },
    {
      label: intl.messages.patientHomeKey,
      value: DeviceLocations.PATIENT_HOME
    }
  ];
};

export const getDeviceConditionOptions = () => {
  const { intl } = store.getState();
  return [
    {
      label: intl.messages.functionalKey,
      value: DeviceConditions.FUNCTIONAL
    },
    {
      label: intl.messages.faultyKey,
      value: DeviceConditions.FAULTY
    },
    {
      label: intl.messages.brokenKey,
      value: DeviceConditions.BROKEN
    },
    {
      label: intl.messages.lostKey,
      value: DeviceConditions.LOST
    }
  ];
};

export const getDeviceStatusOptions = () => {
  const { intl } = store.getState();
  return [
    {
      label: intl.messages.orderedKey,
      value: DeviceStatus.ORDERED
    },
    {
      label: intl.messages.shippedKey,
      value: DeviceStatus.SHIPPED
    },
    {
      label: intl.messages.deliveredKey,
      value: DeviceStatus.DELIVERED
    },
    {
      label: intl.messages.replacedKey,
      value: DeviceStatus.REPLACED
    },
    {
      label: intl.messages.suspendedKey,
      value: DeviceStatus.SUSPENDED
    },
    {
      label: intl.messages.retiredKey,
      value: DeviceStatus.RETIRED
    }
  ];
};

export const unitClasses: string[] = [
  'waist',
  'height',
  'body_temperature',
  'weight',
  'glucose',
  'A1C'
];

export const glucoseClasses: IMeasurementClassType[] = [
  'glucose_bedtime',
  'glucose_fasting',
  'glucose_midnight',
  'glucose_post_meal',
  'glucose_post_meal_breakfast',
  'glucose_post_meal_lunch',
  'glucose_post_meal_dinner',
  'glucose_pre_meal',
  'glucose_pre_meal_breakfast',
  'glucose_pre_meal_lunch',
  'glucose_pre_meal_dinner',
  'glucose',
  'glucose_unspecified'
];

export const iopClasses: IMeasurementClassType[] = ['IOPRight', 'IOPLeft', 'IOPUnspecified'];

export const baselineClasses: string[] = [
  'pressure',
  'pulse_rate',
  'pulse_rate_post_exercise',
  'weight',
  'dry_weight',
  'bmi',
  'height',
  'waist',
  'fat_ratio',
  'glucose_fasting',
  'glucose_pre_meal',
  'glucose_post_meal',
  'glucose_bedtime',
  'glucose_unspecified',
  'glucose_midnight',
  'glucose_pre_meal_breakfast',
  'glucose_post_meal_breakfast',
  'glucose_pre_meal_lunch',
  'glucose_post_meal_lunch',
  'glucose_pre_meal_dinner',
  'glucose_post_meal_dinner',
  'A1C',
  'body_temperature',
  'blood_oxygen',
  'blood_oxygen_active',
  'respiratoryRate',
  'respiratory_rate_post_exercise',
  'forcedExpiratoryVolume1',
  'forcedExpiratoryVolume1Percent',
  'fivc',
  'steps',
  'activity_minutes',
  'sleep'
];

export const glucoseStatClasses: IMeasurementClassType[] = [
  'glucose_min',
  'glucose_max',
  'glucose_avg'
];

export const classWithBarChartDefault: IMeasurementClassType[] = [
  'steps',
  'sleep',
  'activity_minutes'
];
export const barChartClassBlacklist: IMeasurementClassType[] = ['pressure', ...glucoseClasses];

// order matters for the sleep bar chart - index 0 is the bottom section
export const sleepStages = ['deep', 'light', 'rem', 'awake'];
export type SleepStageKey = (typeof sleepStages)[number];

export const sleepStageLabelKeys: Partial<Record<SleepStageKey, string>> = {
  light: 'sleepLightKey',
  deep: 'sleepDeepKey',
  rem: 'sleepRemKey',
  awake: 'sleepAwakeKey'
};

export const alertTypeTranslationKeys: { [key: string]: string } = {
  medical: 'medicalLabelKey',
  compliance: 'medicalComplianceKey'
};

export const maxTagLength = 30; // maximum length allowed to create new tags (group management)
export const maxPatientAge = 120;

export const getGenderOptions = () => {
  const { intl } = store.getState();
  return [
    {
      value: 'male',
      label: intl.messages.male
    },
    {
      value: 'female',
      label: intl.messages.female
    },
    {
      value: 'other',
      label: intl.messages.other
    },
    {
      value: 'unknown',
      label: intl.messages.unknown
    }
  ];
};

export const allMedicalConditions: IConditionDefinition[] = [
  { code: 'I10', description: 'hypertensionKey', womenOnly: false },
  { code: 'E78.5', description: 'unspecifiedHyperlipidemiaKey', womenOnly: false },
  { code: 'E78.2', description: 'mixedHyperlipidemiaKey', womenOnly: false },
  { code: 'E11.9', description: 'diabetesType2Key', womenOnly: false },
  { code: 'E03.9', description: 'unspecifiedHypoThyroidismKey', womenOnly: false },
  { code: 'I25.10', description: 'atheroscleroticHeartDiseaseKey', womenOnly: false },
  { code: 'J44.9', description: 'COPDKey', womenOnly: false },
  { code: 'K21.9', description: 'gastroesophagealRefluxKey', womenOnly: false },
  { code: 'E11.65', description: 'diabetesType2WithHyperglycemiaKey', womenOnly: false },
  { code: 'E78.0', description: 'pureHyperscholesterolemiaKey', womenOnly: false },
  { code: 'G47.33', description: 'obstructiveSleepApneaKey', womenOnly: false },
  { code: 'M81.0', description: 'ageRelatedOsteoporosisKey', womenOnly: false },
  { code: 'E55.9', description: 'unspecifiedVitaminDDeficiencyKey', womenOnly: false },
  { code: 'N18.30', description: 'chronicKidneyDiseaseStage3Key', womenOnly: false },
  { code: 'F41.9', description: 'anxietyUnspecifiedKey', womenOnly: false },
  { code: 'I11.9', description: 'hypertensiveHeartDiseaseKey', womenOnly: false },
  { code: 'F32.9', description: 'depressionUnspecifiedKey', womenOnly: false },
  { code: 'M19.90', description: 'osteoarthritisUnspecifiedKey', womenOnly: false },
  { code: 'I12.9', description: 'hypertensiveChronicKidneyDiseaseKey', womenOnly: false },
  { code: 'E66.9', description: 'obesityKey', womenOnly: false },
  { code: 'I50.9', description: 'CHFKey', womenOnly: false },
  { code: 'J45.901', description: 'asthmaKey', womenOnly: false },
  { code: 'K50.90', description: 'IBDDiagnosedTitleKey', womenOnly: false },
  { code: 'N18.9', description: 'kidneyDisease', womenOnly: false },
  { code: 'E10.9', description: 'diabetesType1Key', womenOnly: false },
  { code: 'M32.9', description: 'sleMedicalCondition', womenOnly: false },
  { code: 'E11.40', description: 'diabetesType2WithNeuropathyKey', womenOnly: false },
  { code: 'R06.02', description: 'shortnessOfBreathKey', womenOnly: false },
  { code: 'I27.20', description: 'pulmonaryHypertensionKey', womenOnly: false },
  { code: 'I48.20', description: 'chronicAtrialFibrillationKey', womenOnly: false },
  { code: 'I48.91', description: 'unspecifiedAtrialFibrillationKey', womenOnly: false },
  { code: 'I48.0', description: 'paroxysmalAtrialFibrillationKey', womenOnly: false },
  { code: 'I50.22', description: 'systolicHeartFailureKey', womenOnly: false },
  { code: 'I50.32', description: 'diastolicHeartFailureKey', womenOnly: false },
  { code: 'I65.23', description: 'occlusionStenosisCarotidKey', womenOnly: false },
  { code: 'J44.1', description: 'COPDWithExacerbationKey', womenOnly: false },
  { code: 'Z79.4', description: 'longTermInsulinKey', womenOnly: false },
  { code: 'Z68.34', description: 'bmi3439Key', womenOnly: false },
  { code: 'R29.6', description: 'repeatedFallsKey', womenOnly: false },
  { code: 'I20.89', description: 'otherAnginaKey', womenOnly: false },
  { code: 'R03.0', description: 'highBPNoHypertensionKey', womenOnly: false },
  { code: 'Z86.79', description: 'otherDiseaseHistoryKey', womenOnly: false },
  // OB/GYN
  { code: 'O24.4', description: 'diabetesGestationalKey', womenOnly: true },
  { code: 'O24.011', description: 'preExistingDiabetesType1Pregnancy1TKey', womenOnly: true },
  { code: 'O24.012', description: 'preExistingDiabetesType1Pregnancy2TKey', womenOnly: true },
  { code: 'O24.013', description: 'preExistingDiabetesType1Pregnancy3TKey', womenOnly: true },
  { code: 'O24.111', description: 'preExistingDiabetesType2Pregnancy1TKey', womenOnly: true },
  { code: 'O24.112', description: 'preExistingDiabetesType2Pregnancy2TKey', womenOnly: true },
  { code: 'O24.113', description: 'preExistingDiabetesType2Pregnancy3TKey', womenOnly: true },
  { code: 'O24.410', description: 'gestationalDiabetesDietControlledKey', womenOnly: true },
  { code: 'O24.414', description: 'gestationalDiabetesInsulinControlledKey', womenOnly: true },
  { code: 'O13.9', description: 'pregnancyHypertension', womenOnly: true },
  { code: 'O24.9', description: 'pregnancyKey', womenOnly: true },
  { code: 'O14.9', description: 'conditionPreeclampsia', womenOnly: true },
  { code: 'O09.299', description: 'supervisionOfPregnancyKey', womenOnly: true },
  { code: 'O10.919', description: 'unspecifiedPreExistingHypertensionKey', womenOnly: true },
  { code: 'Z87.59', description: 'historyOfComplicationsKey', womenOnly: true },
  { code: 'O16.1', description: 'unspecified1THypertensionKey', womenOnly: true },
  { code: 'O16.2', description: 'unspecified2THypertensionKey', womenOnly: true }
];

export const getMedicalConditionOptions = (intl: any, gender: string) => {
  const conditions: ISelectOption[] = [];
  allMedicalConditions
    .sort((a, b) => a.code.localeCompare(b.code))
    .forEach((condition) => {
      if (!condition.womenOnly || (gender === '2' && condition.womenOnly)) {
        conditions.push({
          label: `${condition.code} - ${intl.formatMessage({ id: condition.description })}`,
          value: condition.description
        });
      }
    });
  return conditions;
};

export const diabetesKeys = [
  'diabetesType1Key',
  'diabetesType2Key',
  'diabetesGestationalKey',
  'preExistingDiabetesType1Pregnancy1TKey',
  'preExistingDiabetesType1Pregnancy2TKey',
  'preExistingDiabetesType1Pregnancy3TKey',
  'preExistingDiabetesType2Pregnancy1TKey',
  'preExistingDiabetesType2Pregnancy2TKey',
  'preExistingDiabetesType2Pregnancy3TKey',
  'gestationalDiabetesDietControlledKey',
  'gestationalDiabetesInsulinControlledKey'
];

export const isAdult = (date: Date) => {
  return DateTime.fromJSDate(date).diffNow('years').years < -18;
};

export const defaultFont = 'Open Sans';

export const Operator = [
  {
    string: '!=',
    code: OperatorCode.NOT_EQUAL
  },
  {
    string: '=',
    code: OperatorCode.EQUAL
  },
  {
    string: '<',
    code: OperatorCode.LESS_THAN
  },
  {
    string: '≤',
    code: OperatorCode.LESS_THAN_OR_EQUAL
  },
  {
    string: '>',
    code: OperatorCode.GREATER_THAN
  },
  {
    string: '≥',
    code: OperatorCode.GREATER_THAN_OR_EQUAL
  },
  {
    string: '',
    code: OperatorCode.IN
  }
];

export enum QuestionnaireScope {
  PRIVATE = 'private',
  MANAGED = 'managed',
  ALL = 'all'
}

export const getOperator = (code: string | number) =>
  Operator.find((x) => x.code.toString() === code.toString())?.string;

export const OrgStatusDisclaimer = [
  {
    string: 'dunningDisclaimerKey',
    status: OrgStatus.DUNNING,
    color: 'danger'
  },
  {
    string: 'outstandingInvoicesDisclaimerKey',
    status: OrgStatus.OUTSTANDING_INVOICES,
    color: 'warning'
  },
  {
    string: 'suspendedDisclaimerKey',
    status: OrgStatus.SUSPENDED,
    color: 'danger'
  },
  {
    // Not danger: the practice has done nothing wrong and the remedy is ours. Without an entry
    // here the lookup returned undefined, so a held organisation saw no banner at all and then met
    // a 401 on every call — locked out with nothing on screen saying why.
    string: 'verificationPendingDisclaimerKey',
    status: OrgStatus.VERIFICATION_PENDING,
    color: 'warning'
  }
];

export const getOrgStatusDisclaimerMsg = (status: string | number) =>
  OrgStatusDisclaimer.find((x) => x.status.toString() === status.toString())?.string;

export const getOrgStatusDisclaimerColor = (status: string | number) =>
  OrgStatusDisclaimer.find((x) => x.status.toString() === status.toString())?.color;

export const statusToString = (status: PatientStatus) => {
  switch (status) {
    case PatientStatus.REFERRED:
      return 'referredKey';
    case PatientStatus.DEVICE_ORDERED:
      return 'devicesOrderedKey';
    case PatientStatus.DEVICE_SHIPPED:
      return 'devicesShippedKey';
    case PatientStatus.FIRST_READING_TRANSMITTED:
      return 'firstReadingTransmittedKey';
    case PatientStatus.ENROLLED:
      return 'enrolledKey';
    case PatientStatus.PAUSED:
      return 'pausedKey';
    case PatientStatus.TO_BE_ARCHIVED:
      return 'toBeArchivedKey';
    case PatientStatus.ARCHIVED:
      return 'archivedKey';
    default:
      return '';
  }
};

export const fileMaxSize10mb = 10485760;
export const uncompressedFileSizeLimit = 5242880; // 5Mb
export const compressedFileSizeLimit = 1024000; // 1Mb
export const clinicianImageResolution = 128; // default resolution for clinicians images (128x128)
export const groupImageResolution = 512; // default resolution for group image (512x512)

export const defaultDateRange: IPeriod = 'previous-month';

export const EPIC_EHR = 'EPIC';
export const TIME_LOG_UNDEFINED_TYPE = '14053ff2-b0e0-4e84-a57b-3e850d6aaafa';
export const codeAdminNoGroup = 14;

export const BROWSER_REFRESH_TIMEOUT = 15000; // 15s

export const TABLE_EMPTY_VALUE = '--';
export const TABLE_MAX_TAGS_DISPLAY = 5;
export const TABLE_MAX_AVATARS_DISPLAY = 4;

export const ARCHIVED_LOCK_DAYS = 30;

export const smartMeterManufacturerNames = ['iGlucose', 'Smart Meter'];
export const BODYTRACE_MANUFACTURER_NAME = 'BodyTrace';
export const TRANSTEK_MANUFACTURER_NAME = 'Transtek';
export const actionableDeviceManufacturers = smartMeterManufacturerNames.concat([
  BODYTRACE_MANUFACTURER_NAME
]);
export const resyncableDeviceManufacturers = smartMeterManufacturerNames.concat([
  BODYTRACE_MANUFACTURER_NAME,
  TRANSTEK_MANUFACTURER_NAME
]);
export const SMS_CONSENT_SURVEY_ID = '969724eb-de5b-41d1-8704-9faedecbae5b';
export const SMS_CONSENT_QUESTION_ID = '700f6eea-7100-4362-b200-af3787e31d85';
export const SMS_CONSENT_YES_OPTION_ID = 'bc320975-07ce-417c-a885-0b07ac7fba8e';
